import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { Trash } from 'iconsax-react';
import type { FC, ReactElement } from 'react';

import { type FileEntityType, fileEntityPathSegment } from '@core/types/file';
import { formatAttachmentBytes } from '@core/utils/emailAttachments';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { FileTypeIcon } from './FileTypeIcon';
import type { FileUploadsManager } from './useFileUploads';

type Props = {
  entityType: FileEntityType;
  sourceEntityId: string;
  manager: FileUploadsManager;
};

export const CloneFilesList: FC<Props> = ({
  entityType,
  sourceEntityId,
  manager,
}): ReactElement | null => {
  const theme = useTheme();

  const {
    data: files,
    isError,
    refetch,
  } = useSlabQuery('GET entity files by entity ID', {
    pathParams: { entityPath: fileEntityPathSegment(entityType), id: sourceEntityId },
    queryParams: { page: 0, perPage: 1000 },
  });

  if (isError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='secondary'
          sx={{ fontSize: '0.75rem', color: theme.palette.SlabRed[600] }}
        >
          Could not load the files attached to this {entityType}. They will all be copied to the new
          one unless you load them and remove the ones you don&apos;t want.
        </Typography>
        <Button variant='slab-outline' size='small' onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  const pendingCopies = (files?.items ?? []).filter(
    (file) => !manager.excludedCloneFileIds.includes(file.id),
  );

  if (pendingCopies.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        variant='secondary'
        sx={{ fontSize: '0.75rem', color: theme.palette.SlabGray[600] }}
      >
        These files will be copied to the new {entityType}.
      </Typography>
      {pendingCopies.map((file) => (
        <Box
          key={file.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: theme.palette.SlabGray[200],
          }}
        >
          <FileTypeIcon fileName={file.fileName} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' fontWeight={600} noWrap>
              {file.fileName}
            </Typography>
            <Typography variant='secondary' sx={{ fontSize: '0.75rem' }}>
              {formatAttachmentBytes(file.sizeBytes)} &middot; Will be copied
            </Typography>
          </Box>
          <IconButton
            size='small'
            aria-label={`Remove ${file.fileName}`}
            onClick={() => manager.excludeCloneFile(file.id)}
          >
            <Trash variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};
